import { Button } from '@/components/Button';
import type { ProcessedImage } from '@/utils/types';
import { useCallback, useState } from 'react';
import { FanOutPics } from '../../components/FanOutPics';

interface ShareStepProps {
    processedImages?: ProcessedImage[] | null;
    onRetry?: () => void;
}

export const ShareStep = ({ processedImages: processedImages, onRetry }: ShareStepProps) => {
    const [isSharing, setIsSharing] = useState(false);
    const [shareError, setShareError] = useState<string | null>(null);

    console.log('processedImages', processedImages?.map((image) => image.templateName));

    const handleShareAll = useCallback(async () => {
        if (!processedImages || processedImages.length === 0) return;

        setIsSharing(true);
        setShareError(null); // Clear any previous errors

        try {
            // Try to share multiple files if supported
            if (navigator.share && navigator.canShare) {
                try {
                    const files: File[] = [];

                    for (let i = 0; i < processedImages.length; i++) {
                        if (processedImages[i]?.dataUrl) {
                            const response = await fetch(processedImages[i].dataUrl);
                            const blob = await response.blob();
                            const file = new File([blob], `ocd-awareness-slide-${i + 1}.png`, { type: 'image/png' });
                            files.push(file);
                        }
                    }

                    const shareData = {
                        title: 'My OCD Story',
                        text: 'Breaking the silence on my OCD Story, join me to raise awareness! #MyOCDStory #OCDAwarenessWeek #OCDWeek',
                        files: files
                    };

                    if (navigator.canShare(shareData)) {
                        await navigator.share(shareData);
                        return; // Success - no error
                    }
                } catch (shareError) {
                    console.log('Web Share API failed for multiple files', shareError);
                    // Don't return here, fall through to set error state
                }
            }

            // If we reach here, sharing failed or isn't supported
            setShareError('Sharing failed. You can download the images instead.');
        } catch (error) {
            console.error('Error sharing all:', error);
            setShareError('Sharing failed. You can download the images instead.');
        } finally {
            setIsSharing(false);
        }
    }, [processedImages]);


    if (!processedImages || processedImages.length === 0) {
        return null;
    }

    return (
        <>
            <div className="relative flex justify-center items-center w-full overflow-y-auto overflow-x-hidden px-4" >
                <FanOutPics
                    image1={<img
                        src={processedImages[0].dataUrl}
                    />}
                    image2={<img
                        src={processedImages[1].dataUrl}
                    />}
                    image3={<img
                        src={processedImages[2].dataUrl}
                    />} />
            </div>
            <div className="shrink-0 flex flex-col gap-2 w-full px-8">
                {shareError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                        <p className="text-red-700 text-sm text-center font-medium">
                            {shareError}
                        </p>
                    </div>
                )}
                <Button
                    label={isSharing ? "Sharing..." : "Share Carousel"}
                    onClick={handleShareAll}
                    inactive={isSharing}
                />
                {onRetry && (
                    <Button
                        label="Something wrong? Retry!"
                        onClick={onRetry}
                        variant="outlined"
                    />
                )}
            </div>
        </>
    );
};
